import { ComponentFactoryResolver, Injectable } from '@angular/core';
import { ApolloClient } from '@apollo/client/core';
import { TranslateService } from '@ngx-translate/core';
import { get } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { CustomTranslateHttpLoader } from '../../translate/class/custom-translate-http-loader';
import { MicroApp } from '../class/micro-app.class';
import { Config } from '../model/config.model';
import { HostApi } from '../model/host-api';
import { RoutePartsInstance } from '../util/route-parts.interface';

(window as any).$appShell = (window as any).$appShell || {};
(window as any).$appShell.host = (window as any).$appShell.host || {
  initialized: new BehaviorSubject(false),
  features: undefined as any,
  entitlements: undefined as any,
};

export interface RoutingOptions {
  reload?: boolean;
  noPush?: boolean;
  // redirectTo?: string,
  root?: boolean;
  queryParams?: Record<string, any>;
}

// if it is string, it is used redirect url
// TODO: we should move this as RoutingOptions.redirect
export type TempRoutingOptions = RoutingOptions | string;

// most members of this class is filled up in
// file projects/app-shell/src/app/system/micro-app/service/micro-app.service.ts
// private configureMicroAppService(element, service: AppHost, routingEvent) {
@Injectable({
  providedIn: 'root',
})
export class AppHost implements HostApi {
  public activeMicroApp: MicroApp;
  private activeComponentName: string;
  public packagePath: string;
  public params = new BehaviorSubject<any>(null);
  public initialized = new BehaviorSubject<boolean>(false);

  public get assetPath() {
    return `${this.packagePath}/assets`;
  }

  active: Element;

  public config: Config;

  public routing: {
    navigate: (url: string, options: TempRoutingOptions) => void;
    routes: Observable<any>;
    events: Observable<any>;
    // the following memebers are not implemented, remove it
    // isValid: null as () => boolean,
    // data: null as Observable<any[]>,
    registry: Observable<any>;
    active: Observable<any>;
  };

  public user: {
    info: Observable<{
      userName?: string;
      firmId?: string;
      firmName?: string;
      crdFirmId?: string | number;
    }>;
    profile: Observable<{
      tmp: string;
    }>;
    preferences: Observable<{
      startingRoute: string;
      language: string;
    }>;
    // this member is not implemented, remove it
    // logout?: null as () => void,
    setLanguage: (lang) => void;
  };

  public session: {
    stop: () => void;
    logout: () => void;
  };

  // TODO
  public error: {
    failedToLoad: Observable<any>;
  };

  // TODO
  public info: {
    data: Observable<any>;
  };

  // TODO
  public package: {
    package: Observable<any>;
    registry: Observable<any>;
    // this member is not implemented, remove it
    // data: null as Observable<any>,
  };

  // TODO
  public locking: {
    data: Observable<any>;
  };

  // TODO
  public modal: {
    openAngularComponent: (component: string, componentParams: any, modalParam: any, layer: string) => number;
    openWebComponent: (component: string, componentParams: any, modalParam: any, layer: string) => number;
    close: (modalReferenceId: number, layer: string) => void;
  };

  public popup: {
    openPopup: (component, componentParam, popupParams) => void;
    expand: (index) => void;
  };

  public logging: {
    alert: (level, message, source) => void;
    log: (level, message, source) => void;
    critical: (
      ags: string,
      component: string,
      message: string,
      sessionId?: string,
      userId?: string,
      correlationId?: string,
    ) => Promise<void>;
    error: (
      ags: string,
      component: string,
      message: string,
      sessionId?: string,
      userId?: string,
      correlationId?: string,
    ) => Promise<void>;
    warn: (
      ags: string,
      component: string,
      message: string,
      sessionId?: string,
      userId?: string,
      correlationId?: string,
    ) => Promise<void>;
    info: (
      ags: string,
      component: string,
      message: string,
      sessionId?: string,
      userId?: string,
      correlationId?: string,
    ) => Promise<void>;
  };
  // TODO
  public notification: {
    // this member is not implemented, remove it
    // data: null as Observable<any>,
  };

  // TODO
  public view: {
    // this member is not implemented, remove it
    // data: null as Observable<any>,
    state: Observable<any>;
    setPageTitle: (title: string, source: string, options?: { label?: string }) => void;
  };

  // TODO
  public component: {
    load: (microAppId: string, params: any, targetView: HTMLElement, options?: any) => Promise<HTMLElement>;
  };

  public events: {
    post: (request: {
      /** CorrelationId: null */ correlationId?: string;
      /** ?: DXTGATEW */ ags: string;
      /** Component Name: app-shell */ component: string;
      /** Action: view */ action: string;
      /** Resource: app-shell */ resource: string;
      /** Details: {} */ details: any;
    }) => Promise<any>;
    data: Observable<any[]>;
    sessionId: Observable<any>;
    test: () => void;
  };

  public date: {
    toUser: (date: Date, format: string, timeZone: string) => Promise<string>;
    toServer: (date: string) => string;
  };

  public entitlements: {
    permissions: Observable<string[]>;
    userType: Observable<string>;
    isEnabled: (access: string) => Observable<string | boolean>;
    isAllEnabled: (access: string[]) => Observable<boolean>;
    classification: Observable<string>;
    isClassificationEnabled: (access: string) => Observable<boolean>;
  };

  public features: {
    features: Observable<unknown>;
    isEnabled: (feature: string) => Observable<string | boolean>;
  };

  public toast: {
    open: (param: { theme: string; text: string; fadeFast?: boolean }) => HTMLElement;
  };

  public firmService: {
    setSelectedFirm: (firmId: any, firmName: any, firmCity: any, expires?: any) => void;
    getSelectedFirm: () => { firmId: string; firmName: string; firmCity: string };
    fetchSelectedFirm: () => Observable<{ firmId: string; firmName: string; firmCity: string }>;
  };

  public gql: {
    getClient: (uri, credentials) => ApolloClient<any>;
    query: (param: any) => any;
    mutate: (param: any) => any;
    setClient: any;
    watchQuery: any;
  };
  constructor(
    private translateService: TranslateService,
    private componentFactoryResolver: ComponentFactoryResolver,
  ) {}

  init() {
    CustomTranslateHttpLoader.assetPath = this.assetPath;
    this.initializeUser();
    this.initializeRouting();
    this.initialized.next(true);
    // add global features

    const root = (window as any).$appShell.host;
    root.initialized.pipe(take(1)).subscribe((initialized) => {
      if (!initialized) {
        root.features = this.features;
        root.entitlements = this.entitlements;
        root.initialized.next(true);
      }
    });
  }

  async next() {
    await this.initialized
      .pipe(
        filter((value) => !!value),
        take(1),
      )
      .toPromise();
    return this;
  }

  private refreshActive() {
    const destroyed = get(this, 'activeMicroApp.changeDetectorRef.destroyed');
    if (!destroyed) {
      this.activeMicroApp.changeDetectorRef.detectChanges();
      this.activeMicroApp.changeDetectorRef.markForCheck();
    }
  }

  navigate(commands: string | any[], options?: any) {
    this.config.pipe(take(1)).subscribe((config) => {
      let rootPath = config.basePath;
      if (options) {
        if (options.fromAppHost || options.root) {
          rootPath = '';
        } else if (options.relative) {
          rootPath = config.basePath;
        }
      }

      // redirect starting from the component base url
      if (typeof commands === 'string') {
        this.routing.navigate(`${rootPath}${commands}`, options);
      } else {
        this.routing.navigate(`${rootPath}/${commands.join('/')}`, options);
      }
    });
  }

  private loadInternalPath(internalRoute: string, params: any) {
    if (!internalRoute) {
      internalRoute = '/';
    }

    const match = RoutePartsInstance.findMatch(this.activeMicroApp.routes, internalRoute);
    if (!match) {
      console.log('invalid route', internalRoute);
      return;
    }

    if (match.route.component !== this.activeComponentName) {
      const componentFactory = this.componentFactoryResolver.resolveComponentFactory(match.route.component);
      if (this.activeMicroApp) {
        this.activeMicroApp.anchor.viewContainerRef.clear();
        this.activeMicroApp.anchor.viewContainerRef.createComponent(componentFactory);
      }
    }
    this.params.next(match.param);
    this.refreshActive();
  }

  private initializeUser() {
    this.user.preferences.subscribe((preferences) => {
      this.translateService.onLangChange.pipe(take(1)).subscribe(() => {
        this.refreshActive();
      });
      this.translateService.use((preferences && preferences.language) || 'en');
      setTimeout(() => this.refreshActive());
    });
  }

  private initializeRouting() {
    if (this.activeMicroApp && this.activeMicroApp.routes) {
      this.routing.events.subscribe((event) => {
        if (event && event.type === 'end') {
          const full = RoutePartsInstance.toParts(event.url);
          const base = RoutePartsInstance.toParts(event.routePath);
          full.splice(0, base.length);
          const internalPath = `/${full.join('/')}`;
          this.loadInternalPath(internalPath, event.params);
        }
      });
    }
  }
}
